import './settings-leaf.css';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { photoQuoteApi } from '../../api/endpoints.ts';
import { useSession } from '../../state/session.tsx';
import { Alert, Button, Checkbox, LoadingBlock, Select, TextArea } from '../ui.tsx';
import { NumberInput } from '../number-input.tsx';
import { useAiDisclosure } from '../ai-disclosure.tsx';
import {
  PHOTO_QUOTE_RANGE_WIDTH_MAX,
  PHOTO_QUOTE_RANGE_WIDTH_MIN,
  PHOTO_QUOTE_RISK_PRESETS,
  PHOTO_QUOTE_SLA_MINUTES_MAX,
  PHOTO_QUOTE_SLA_MINUTES_MIN,
  PHOTO_QUOTE_TERMS_MAX,
  type PhotoQuoteSettings,
} from '@shared/photo-quotes.ts';

/**
 * Settings > Photo quotes (spec WILD-05): the upload page's switch and link,
 * how much risk the owner will take on a firm price, how wide a range is,
 * which rooms a customer must photograph, the firm-price terms, and how fast
 * the office promises an answer.
 *
 * Everyone who can open Settings reads it; only an owner changes it, because
 * the preset and the terms decide what the company can lose on a price it
 * sends (the API answers anyone else 403).
 */
export default function PhotoQuotesCard() {
  const { t } = useTranslation();
  const { can, user } = useSession();
  const queryClient = useQueryClient();
  const canEdit = user?.role === 'owner' && can('company:write');
  const disclosure = useAiDisclosure();
  const [draft, setDraft] = useState<PhotoQuoteSettings | null>(null);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);

  const query = useQuery({
    queryKey: ['photo-quote-settings'],
    queryFn: () => photoQuoteApi.settings(),
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (!query.data) return;
    const {
      uploadPath: _path,
      defaultTerms: _terms,
      maxPhotos: _max,
      readingUnavailableReason: _reason,
      ...settings
    } = query.data;
    setDraft(settings);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (settings: PhotoQuoteSettings) => photoQuoteApi.updateSettings(settings),
    onSuccess: (view) => {
      setSaved(true);
      queryClient.setQueryData(['photo-quote-settings'], view);
    },
  });

  function set<K extends keyof PhotoQuoteSettings>(key: K, value: PhotoQuoteSettings[K]) {
    setSaved(false);
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  const view = query.data;
  const uploadUrl = view?.uploadPath ? `${window.location.origin}${view.uploadPath}` : null;

  return (
    <section className="card work-section settings-leaf mb-6" aria-labelledby="photo-quotes-settings-heading">
      <div className="work-section-heading">
        <h2 id="photo-quotes-settings-heading" className="text-lg font-semibold">
          {t('photoQuotes.settings.heading')}
        </h2>
      </div>
      <p className="mt-2 text-sm text-[color:var(--color-muted)]">{t('photoQuotes.settings.description')}</p>

      {query.isLoading ? <LoadingBlock label={t('photoQuotes.settings.loading')} /> : null}
      {query.isError ? (
        <div className="mt-4">
          <Alert tone="error" title={t('photoQuotes.settings.loadError')}>
            {(query.error as Error).message}
          </Alert>
        </div>
      ) : null}

      {view && draft ? (
        <form
          className="mt-5"
          onSubmit={(event) => {
            event.preventDefault();
            save.mutate({ ...draft, terms: draft.terms?.trim() ? draft.terms : null });
          }}
        >
          <fieldset disabled={!canEdit}>
            <Checkbox
              label={t('photoQuotes.settings.enabled')}
              hint={t('photoQuotes.settings.enabledHint', { count: view.maxPhotos })}
              checked={draft.enabled}
              onChange={(event) => set('enabled', event.target.checked)}
            />

            <div className="mb-4 rounded border border-[color:var(--color-line)] p-3 text-sm">
              {uploadUrl ? (
                <>
                  <p className="mb-1 font-medium">{t('photoQuotes.settings.linkLabel')}</p>
                  <p className="flex flex-wrap items-center gap-2 break-all">
                    <a href={uploadUrl} target="_blank" rel="noreferrer" className="underline">
                      {uploadUrl}
                    </a>
                    <Button
                      variant="ghost"
                      onClick={() => {
                        void navigator.clipboard?.writeText(uploadUrl).then(() => setCopied(true));
                      }}
                    >
                      {copied ? t('common.copied') : t('photoQuotes.settings.copyLink')}
                    </Button>
                  </p>
                  <p className="mt-1 text-[color:var(--color-muted)]">{t('photoQuotes.settings.linkHint')}</p>
                </>
              ) : (
                <p>
                  {t('photoQuotes.settings.noLink')}
                  {/* The web form section renders for company:write only. */}
                  {can('company:write') ? (
                    <>
                      {' '}
                      <Link to="/settings#settings-widget" className="font-semibold underline">
                        {t('photoQuotes.settings.noLinkCta')}
                      </Link>
                    </>
                  ) : null}
                </p>
              )}
            </div>

            {view.readingUnavailableReason ? (
              <div className="mb-4">
                <Alert tone="info">
                  <p>{t(`photoQuotes.settings.reading.${view.readingUnavailableReason}`)}</p>
                  {/* Only an owner's acceptance lets customer photos be read. */}
                  {view.readingUnavailableReason === 'disclosure' && canEdit ? (
                    <p className="mt-2">
                      <Button
                        onClick={() =>
                          void disclosure
                            .ensure(true)
                            .then(() => queryClient.invalidateQueries({ queryKey: ['photo-quote-settings'] }))
                            .catch(() => undefined)
                        }
                      >
                        {t('photoQuotes.settings.readingAccept')}
                      </Button>
                    </p>
                  ) : null}
                </Alert>
              </div>
            ) : null}
            {disclosure.dialog}

            <fieldset className="mt-2 border-t border-[color:var(--color-line)] pt-5">
              <legend className="mb-3 text-base font-semibold">{t('photoQuotes.settings.riskHeading')}</legend>
              <Select
                label={t('photoQuotes.settings.preset')}
                hint={t(`photoQuotes.settings.presetHint.${draft.riskPreset}`)}
                value={draft.riskPreset}
                options={PHOTO_QUOTE_RISK_PRESETS.map((preset) => ({
                  value: preset,
                  label: t(`photoQuotes.settings.presets.${preset}`),
                }))}
                onChange={(event) => set('riskPreset', event.target.value as PhotoQuoteSettings['riskPreset'])}
              />
              <div className="grid gap-x-4 sm:grid-cols-2">
                <NumberInput
                  label={t('photoQuotes.settings.rangeWidth')}
                  hint={t('photoQuotes.settings.rangeWidthHint')}
                  min={PHOTO_QUOTE_RANGE_WIDTH_MIN}
                  max={PHOTO_QUOTE_RANGE_WIDTH_MAX}
                  value={draft.rangeWidthPct}
                  onValueChange={(value) => set('rangeWidthPct', value ?? draft.rangeWidthPct)}
                />
                <NumberInput
                  label={t('photoQuotes.settings.sla')}
                  hint={t('photoQuotes.settings.slaHint')}
                  min={PHOTO_QUOTE_SLA_MINUTES_MIN}
                  max={PHOTO_QUOTE_SLA_MINUTES_MAX}
                  value={draft.slaMinutes}
                  onValueChange={(value) => set('slaMinutes', value ?? draft.slaMinutes)}
                />
              </div>
            </fieldset>

            <fieldset className="mt-2 border-t border-[color:var(--color-line)] pt-5">
              <legend className="mb-3 text-base font-semibold">{t('photoQuotes.settings.coverageHeading')}</legend>
              <Checkbox
                label={t('photoQuotes.settings.requireKitchen')}
                checked={draft.requireKitchen}
                onChange={(event) => set('requireKitchen', event.target.checked)}
              />
              <Checkbox
                label={t('photoQuotes.settings.requireEachBathroom')}
                checked={draft.requireEachBathroom}
                onChange={(event) => set('requireEachBathroom', event.target.checked)}
              />
              <Checkbox
                label={t('photoQuotes.settings.requireEachBedroom')}
                checked={draft.requireEachBedroom}
                onChange={(event) => set('requireEachBedroom', event.target.checked)}
              />
            </fieldset>

            <fieldset className="mt-2 border-t border-[color:var(--color-line)] pt-5">
              <legend className="mb-3 text-base font-semibold">{t('photoQuotes.settings.termsHeading')}</legend>
              <TextArea
                label={t('photoQuotes.settings.terms')}
                hint={t('photoQuotes.settings.termsHint')}
                rows={4}
                maxLength={PHOTO_QUOTE_TERMS_MAX}
                placeholder={view.defaultTerms}
                value={draft.terms ?? ''}
                onChange={(event) => set('terms', event.target.value)}
              />
            </fieldset>
          </fieldset>

          {save.isError ? (
            <div className="mb-3">
              <Alert tone="error">{save.error.message}</Alert>
            </div>
          ) : null}
          {canEdit ? (
            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" busy={save.isPending}>
                {t('photoQuotes.settings.save')}
              </Button>
              {saved ? (
                <span className="text-sm text-[color:var(--color-muted)]">{t('photoQuotes.settings.saved')}</span>
              ) : null}
            </div>
          ) : (
            <p className="text-sm text-[color:var(--color-muted)]">{t('photoQuotes.settings.ownerOnly')}</p>
          )}
        </form>
      ) : null}
    </section>
  );
}
